import { Helmet } from "react-helmet-async";
import React, { useEffect, useState, useMemo } from "react";
import { getFaqPageContent } from "services/getFaqPageContent";
import { useLanguage } from "context/languageContext";
import { useTranslation } from "react-i18next";
import styles from "./FaqPage.module.scss";

const FAQPage = () => {
  const [activeQuestions, setActiveQuestions] = useState({});
  const { language } = useLanguage(); // Use the LanguageContext to get current language
  const [faqData, setFaqData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation("global");

  // Fetch FAQ data on component mount
  useEffect(() => {
    const fetchFAQData = async () => {
      try {
        const data = await getFaqPageContent(language);
        setFaqData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQData();
  }, [language]);

  // Toggle the active state of a question
  const toggleActiveQuestion = (sectionIndex, questionIndex) => {
    setActiveQuestions((prev) => ({
      ...prev,
      [`${sectionIndex}-${questionIndex}`]:
        !prev[`${sectionIndex}-${questionIndex}`],
    }));
  };

  // Scroll smoothly to the specified section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Generate anchor ID from section title
  const generateAnchorId = (sectionTitle) =>
    sectionTitle.toLowerCase().replace(/\s+/g, "-");

  // Get SEO data from translations
  const metaTitle = t("faq.headTitle");
  const metaDescription = t("faq.metaDescription");
  const metaKeywords = t("faq.metaKeywords");

  // Get current canonical URL
  const currentPath = window.location.pathname;
  const baseUrl = "https://chooseandwork.com";
  const canonicalUrl = `${baseUrl}${currentPath}`;

  // Open Graph image
  const ogImage = "https://chooseandwork.com/og-image-faq.jpg";
  const twitterImage = "https://chooseandwork.com/twitter-image-faq.jpg";

  // Determine locale
  const currentLocale = language === 'fr' ? 'fr_FR' : language === 'en' ? 'en_US' : 'es_ES';

  // FAQ Section component
  const FAQSection = ({ section }) => {
    return (
      <div className={styles.faqContentSection} id={`content-${section.index}`}>
        <h2 className={styles.faqContentSectionTitle}>
          {section.section_title}
        </h2>
        <p
          className={styles.faqContentSectionBody}
          dangerouslySetInnerHTML={{ __html: section.section_body }}
        ></p>
        <ul className={styles.faqContentSectionQuestions}>
          {section.qa
            .sort((a, b) => a.index - b.index)
            .map((question) => (
              <li
                key={question.index}
                id={`q${section.index}-${question.index}`}
                className={`${styles.faqContentItem} ${
                  activeQuestions[`${section.index}-${question.index}`]
                    ? styles.active
                    : ""
                }`}
              >
                <p
                  className={styles.faqContentQuestion}
                  onClick={() =>
                    toggleActiveQuestion(section.index, question.index)
                  }
                  dangerouslySetInnerHTML={{ __html: question.question }}
                />
                <p
                  className={styles.faqContentAnswer}
                  dangerouslySetInnerHTML={{ __html: question.answer }}
                />
              </li>
            ))}
        </ul>
      </div>
    );
  };

  // Memoized anchor menu generation
  const anchorMenu = useMemo(() => {
    return faqData?.data.map((faq) => (
      <li className={styles.faqMenuSection} key={faq.index}>
        <a
          onClick={() => scrollToSection(`content-${faq.index}`)}
          href={`#${generateAnchorId(faq.section_title)}`}
        >
          <h3 className={styles.faqMenuSectionTitle}>{faq.section_title}</h3>
        </a>
      </li>
    ));
  }, [faqData]);

  // Render loading state, error messages, or the FAQ content
  if (loading) return <div>Loading...</div>; // Replace with a skeleton loader if desired
  if (error) return <p>Error: {error}</p>;

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={currentLocale} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={twitterImage} />
        
        {/* Additional meta tags */}
        <meta name="robots" content="index, follow" />
        
        {/* FAQPage Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "name": metaTitle,
            "description": metaDescription,
            "url": canonicalUrl,
            "mainEntity": faqData?.data.flatMap((section, sectionIndex) => 
              section.qa.map((question, questionIndex) => ({
                "@type": "Question",
                "name": question.question.replace(/<[^>]*>/g, ''), // Remove HTML tags
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": question.answer.replace(/<[^>]*>/g, '') // Remove HTML tags
                }
              }))
            ) || []
          })}
        </script>
        
        {/* Breadcrumb Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": t("home.title"),
                "item": "https://chooseandwork.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": t("faq.title"),
                "item": canonicalUrl
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={`${styles.faqPage} pageContainer`}>
        <div className={styles.row}>
          <nav className={styles.faqMenu}>
            <ul className={styles.faqMenuSections}>{anchorMenu}</ul>
          </nav>
          <div className={styles.faqContent}>
            <div className={styles.faqContentSections}>
              {faqData?.data.map((section) => (
                <FAQSection key={section.index} section={section} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQPage;